import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { getServiceStatusInfo } from '../../../api/resilience/resilience.api';
import { type ServiceStatusInfo } from '../types';
import { useIsoLanguageCode } from '../../../utils/hooks/useIsoLanguageCode';
import { useGetAccountDetailsQuery } from '../../../queries/useGetAccountDetailsQuery';
import useWizerQuery from '../../training/useWizerQuery';
import { useMeQuery } from '../../../components/hooks/useMeQuery';
import { useActor } from '../../../components/hooks/useActor';

export const SERVICE_STATUS_INFO_QUERY_KEY = '/v1/service/status';

export const useServiceStatusInfo = () => {
  const isoLanguageCode = useIsoLanguageCode();
  const { data: me } = useMeQuery();
  const accountId = me?.account?.id;
  const actor = useActor();

  // Check if this is a Pool tenant
  const isPoolTenant = actor.isPool;

  // For Pool tenants, we don't need account details or Wizer integration
  const { data: account } = useGetAccountDetailsQuery({
    accountId: isPoolTenant ? undefined : accountId,
  });

  const { data: wizer, isLoading } = useWizerQuery({
    accountId: isPoolTenant ? undefined : accountId,
    cbid: account?.cbid,
    email: actor?.email,
  });

  const isWizerConnected = Boolean(wizer?.dashboardMagicLink);

  return useQuery<ServiceStatusInfo[], AxiosError>({
    queryKey: [
      SERVICE_STATUS_INFO_QUERY_KEY,
      { isoLanguageCode, isWizerConnected, accountId, isPoolTenant },
    ],
    queryFn: async () => {
      const params = { isoLanguageCode, isWizerConnected };
      return getServiceStatusInfo({ params })
        .then((res) => res.data)
        .then((serviceStatusInfo) => {
          return serviceStatusInfo.filter(
            (serviceStatusItem) =>
              serviceStatusItem.product && serviceStatusItem.productFamily
          );
        });
    },
    enabled: !!isoLanguageCode && (isPoolTenant || (!!accountId && !isLoading)),
    refetchOnWindowFocus: true,
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 15, // 15 minutes
  });
};

useServiceStatusInfo.queryKey = SERVICE_STATUS_INFO_QUERY_KEY;
